import React, { useState, useEffect } from 'react';
import AuthShell from './AuthShell';
import TOSpopup from './TermsModal';
import ClickwrapModal from './ClickwrapModal';
import { LegalDocumentBody } from './LegalPage';
import { TERMS_OF_USE, PRIVACY_POLICY, EULA, LEGAL_ENTITY, LEGAL_LAST_UPDATED } from './legalDocs';
import { useNavigate } from 'react-router-dom';

// Terms of Use text shown in the signup modal — rendered from legalDocs.js,
// the same source as the /terms-of-use page, so the two never drift apart.
const TermsOfUseModalContent = () => (
  <>
    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
      <h1 style={{ fontSize: '24px', margin: '0 0 10px 0', fontFamily: 'Judson, serif' }}>TERMS OF USE</h1>
      <p style={{ fontWeight: 'bold', fontSize: '14px', margin: '0 0 4px' }}>{LEGAL_ENTITY}</p>
      <p style={{ fontStyle: 'italic', fontSize: '13px', margin: 0 }}>Last Updated: {LEGAL_LAST_UPDATED}</p>
    </div>
    <LegalDocumentBody doc={TERMS_OF_USE} headingStyle={{ fontSize: '18px', marginTop: '20px' }} />
  </>
);

// EULA / Privacy Policy links beside the agree checkbox open in a new tab so
// the half-filled signup form is left untouched.
const LegalTabLink = ({ doc }) => (
  <a
    href={doc.path}
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: '#4A8EB3', textDecoration: 'underline' }}
  >
    {doc.linkLabel}
  </a>
);

const EyeIcon = ({ visible }) =>
  visible ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

const SignUpFront = ({ setLoggedInUser }) => {
  // --- SHARED STATE ---
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [dob, setDob] = useState("");
  const [age, setAge] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [isTOSAccepted, setIsTOSAccepted] = useState(false);
  const [error, setError] = useState("");

  // --- PRODUCTION STATE ---
  const [showVerification, setShowVerification] = useState(false);
  const [verifCode, setVerifCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isFocused, setIsFocused] = useState(false); // <--- Add this

  // Read the plan chosen on the Pricing page; default to Normal
  const selectedPlan = (() => {
    const p = localStorage.getItem('selectedPlan');
    return ['N', 'A', 'E', 'O'].includes(p) ? p : 'N';
  })();

  const navigate = useNavigate();

  const REGEX = {
    firstName: /^[A-Za-z]{1,15}$/,
    lastName: /^[A-Za-z]{1,20}$/,
    zipCode: /^\d{5}$/,
    // Max raised to 64 (NIST SP 800-63B) and hyphen added to support Apple/Samsung auto-generated passwords
    password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[-!@#$%^&*(),.?":{}|<>]).{8,64}$/
  };

  // ---------------------------------------------------------
  // PRODUCTION LOGIC (Verification)
  // ---------------------------------------------------------

  // --- NEW HELPER: Age Calculator ---
  const calculateAge = (dateString) => {
    if (!dateString) return 0;
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const validateInputsProd = () => {
    if (!REGEX.firstName.test(firstname.trim())) { setError("First Name must be 1 word, max 15 letters."); return false; }
    if (!REGEX.lastName.test(lastname.trim())) { setError("Last Name must be 1 word, max 20 letters."); return false; }

    if (!dob) {
      setError("Please enter your Date of Birth.");
      return false;
    }
    const calculatedAge = calculateAge(dob);

    if (calculatedAge < 18) {
      setError("You must be at least 18 years old to create an account.");
      return false;
    }
    if (calculatedAge > 105) {
      setError("Please enter a valid Date of Birth.");
      return false;
    }

    if (!REGEX.zipCode.test(zipcode)) { setError("ZIP code must be exactly 5 numbers."); return false; }
    if (!REGEX.password.test(password)) { setError("Password must be 8–64 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character."); return false; }
    return true;
  };

  const handleRealSignUp = async (e) => {
    if (e) e.preventDefault();
    setError("");

    if (!isTOSAccepted) { setError("You must acknowledge the Terms of Use to proceed"); return; }
    if (!validateInputsProd()) return;

    try {
      const response = await fetch('https://vicktoria.ai/api/email-verification/send', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        //I think most services don't care about case for emails
        //BUT in case they do we really should let this be as IS
      });

      if (response.ok) {
        setShowVerification(true);
      } else {
        const result = await response.json();
        setError(result.error || "Failed to send code.");
      }
    } catch (err) {
      setError("Connection to server failed.");
    }
  };

  const finalizeRegistration = async () => {
    setIsVerifying(true);
    setError("");

    try {
      const verifCheck = await fetch('https://vicktoria.ai/api/email-verification/verify', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, userCode: verifCode }),
      });

      if (!verifCheck.ok) {
        const result = await verifCheck.json();
        setError(result.error || "Invalid code.");
        setIsVerifying(false);
        return;
      }

      const response = await fetch('https://vicktoria.ai/api/', {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstname,
          lastname,
          email: email.toLowerCase(), // <--- Force lowercase here
          password,
          age: calculateAge(dob),   // Send calculated number (for analytics/search)
          dateOfBirth: dob,         // Send raw date string (for storage)
          zipcode,
          isSpecialPermission: false,
          planCode: selectedPlan
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        let errorMsg = result.error || "Signup failed";

        // Check for the specific MongoDB duplicate key error code (E11000)
        if (errorMsg.includes("E11000") || errorMsg.includes("duplicate key")) {
          errorMsg = "An account with this email already exists.";
        }

        setError(errorMsg);
        setIsVerifying(false);
      } else {
        if (result.token) localStorage.setItem("token", result.token);
        const userData = result.user || result;
        setLoggedInUser(userData);
        localStorage.setItem("loggedInUser", JSON.stringify(userData));
        // --- CHANGED NAVIGATION LOGIC ---
        // Check if there is a saved page, otherwise default to Home ('/')
        const destination = localStorage.getItem('postLoginRedirect') || '/save-as-app';
        navigate(destination);
      }
    } catch (err) {
      setError("Connection to server failed.");
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    if (verifCode.length === 6) finalizeRegistration();
  }, [verifCode]);

  // --- NEW FUNCTIONS FOR VERIFICATION UI ---
  const handleClearEntry = () => {
    setVerifCode("");
  };

  const handleResendCode = async () => {
    setError("");
    try {
      // UPDATED ENDPOINT
      const response = await fetch('https://vicktoria.ai/api/email-verification/resend-signup', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });

      if (response.ok) {
        setError("A new code has been sent to your email.");
      } else {
        const result = await response.json();
        setError(result.error || "Failed to send code.");
      }
    } catch (err) {
      setError("Connection to server failed.");
    }
  };
  // ---------------------------------------------------------
  // DEVELOPMENT LOGIC (Direct SignUp)
  // ---------------------------------------------------------

  const validateInputsDev = () => {
    // 1. First Name
    if (!REGEX.firstName.test(firstname.trim())) {
      setError("First Name must be 1 word, max 15 letters, no numbers or special characters.");
      return false;
    }
    // 2. Last Name
    if (!REGEX.lastName.test(lastname.trim())) {
      setError("Last Name must be 1 word, max 20 letters, no numbers or special characters.");
      return false;
    }
    // 3. Date of birth Logic (calls age to be calculated outside)
    if (!dob) {
      setError("Please enter your Date of Birth.");
      return false;
    }
    const calculatedAge = calculateAge(dob);

    if (calculatedAge < 18) {
      setError("You must be at least 18 years old to create an account.");
      return false;
    }
    if (calculatedAge > 105) {
      setError("Please enter a valid Date of Birth.");
      return false;
    }
    // 4. Zipcode
    if (!REGEX.zipCode.test(zipcode)) {
      setError("ZIP code must be exactly 5 numbers.");
      return false;
    }
    // 5. Password
    if (!REGEX.password.test(password)) {
      setError("Password must be 8–15 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character.");
      return false;
    }
    return true; // All checks passed
  };

  // Renamed to avoid collision with the Prod function, updated in Dev JSX
  const handleDevSignUp = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors

    // 1. Check TOS
    if (!isTOSAccepted) {
      setError("You must acknowledge the Terms of Use to proceed");
      return;
    }

    // 2. Run Validations
    if (!validateInputsDev()) {
      return; // Stop if validation fails
    }

    const newUser = {
      firstname,
      lastname,
      email: email.toLowerCase(), // <--- Force lowercase here
      password,
      age: calculateAge(dob),   // Send calculated number (for analytics/search)
      dateOfBirth: dob,         // Send raw date string (for storage)
      zipcode,
      isSpecialPermission: false,
      planCode: selectedPlan
    };

    try {
      const response = await fetch('https://vicktoria.ai/api/', {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newUser),
      });

      const result = await response.json();

      if (!response.ok) {
        let errorMsg = result.error || "Signup failed";

        // Check for the specific MongoDB duplicate key error code (E11000)
        if (errorMsg.includes("E11000") || errorMsg.includes("duplicate key")) {
          errorMsg = "An account with this email already exists.";
        }

        setError(errorMsg);
      } else {
        if (result.token) {
          localStorage.setItem("token", result.token);
        }

        const userData = result.user || result;

        setLoggedInUser(userData);
        localStorage.setItem("loggedInUser", JSON.stringify(userData));
        // --- CHANGED NAVIGATION LOGIC ---
        // Check if there is a saved page, otherwise default to Home ('/')
        const destination = localStorage.getItem('postLoginRedirect') || '/save-as-app';
        navigate(destination);
      }
    } catch (err) {
      setError("Connection to server failed.");
    }
  };

  const mobileStyles = `
    input[type="date"] {
      -webkit-appearance: none;
      appearance: none;
      line-height: normal;
    }
    input[type="date"]::-webkit-calendar-picker-indicator {
      padding: 0;
      margin: 0;
    }
    input[type="date"]::-webkit-datetime-edit {
      padding: 0;
    }
    @media screen and (max-width: 390px) {
      .mobile-row { gap: 6px !important; }
      .mobile-label { font-size: 13px !important; margin-bottom: 2px !important; }
      .mobile-input { font-size: 12px !important; padding: 8px 4px !important; }
    }
  `;
  // =========================================================
  // THE SWITCH
  // =========================================================

  if (!window.MY_SWITCH.isDev) {
    // --- PRODUCTION RENDERING ---

    // RESTORED STYLES FROM ORIGINAL
    const inputContainerStyle = { marginBottom: '15px', textAlign: 'center', width: '100%' };
    const labelStyle = { display: 'block', color: '#2B4B6F', fontSize: '16px', marginBottom: '5px', fontWeight: '500' };
    const inputStyle = { width: '100%', padding: '10px', height: '42px', borderRadius: '8px', border: '2px solid #D1DEE6', backgroundColor: '#F8FAFC', textAlign: 'center', fontSize: '14px', boxSizing: 'border-box' };
    const passwordInputStyle = { ...inputStyle, paddingRight: '36px' };
    const eyeBtnStyle = { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0', color: '#2B4B6F', display: 'flex', alignItems: 'center' };
    const submitButtonStyle = { backgroundColor: '#4A8EB3', color: 'white', border: 'none', padding: '12px 40px', borderRadius: '12px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', marginTop: '20px' };
    const boxStyle = { width: '45px', height: '55px', border: '1px solid #2B4B6F', display: 'inline-block', margin: '0 5px', backgroundColor: 'white', fontSize: '24px', lineHeight: '55px', textAlign: 'center', verticalAlign: 'top' };

    return (
      <AuthShell title={showVerification ? "Verify Your Email" : "Create Your Account"}>
        <style>{`
          @media screen and (max-width: 600px) {
            .verification-box { width: 35px !important; height: 45px !important; line-height: 45px !important; margin: 0 3px !important; font-size: 18px !important; }
          }
          @media screen and (max-width: 400px) {
            .verification-box { width: 30px !important; height: 40px !important; line-height: 40px !important; margin: 0 3px !important; font-size: 16px !important; }
          }
          @media screen and (max-width: 350px) {
            .verification-box { width: 20px !important; height: 30px !important; line-height: 30px !important; margin: 0 4px !important; font-size: 14px !important; }
          }
            ${mobileStyles}
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          {!showVerification ? (
            <>
              <div className="mobile-row" style={{ display: 'flex', gap: '15px', width: '100%', maxWidth: '320px', marginBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                  <label className="mobile-label" style={labelStyle}>First Name</label>
                  <input className="mobile-input" style={inputStyle} value={firstname} onChange={(e) => setFirstname(e.target.value)} placeholder="First Name" />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="mobile-label" style={labelStyle}>Last Name</label>
                  <input className="mobile-input" style={inputStyle} value={lastname} onChange={(e) => setLastname(e.target.value)} placeholder="Last Name" />
                </div>
              </div>

              <div className="mobile-row" style={{ display: 'flex', gap: '15px', width: '100%', maxWidth: '320px', marginBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                  <label className="mobile-label" style={labelStyle}>Date of Birth</label>
                  <input type="date" className="mobile-input" style={inputStyle} value={dob} onChange={(e) => setDob(e.target.value)} max={new Date().toISOString().split("T")[0]} />
                  {/*max setting prevents any future date from being selected */}
                </div>
                <div style={{ flex: 1 }}>
                  <label className="mobile-label" style={labelStyle}>ZIP code</label>
                  <input className="mobile-input" style={inputStyle} value={zipcode} maxLength={5} onChange={(e) => setZipcode(e.target.value)} placeholder="00000" />
                </div>
              </div>

              <div style={{ ...inputContainerStyle, maxWidth: '320px' }}>
                <label style={labelStyle}>Email Address</label>
                <input style={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
                {selectedPlan === 'E' && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#4a7094', fontStyle: 'italic' }}>
                    Academic plan — an .edu email address is required.
                  </p>
                )}
              </div>

              <div style={{ ...inputContainerStyle, maxWidth: '320px' }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? "text" : "password"} style={passwordInputStyle} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" maxLength={15} />
                  <button style={eyeBtnStyle} onClick={() => setShowPassword(!showPassword)} type="button" tabIndex={-1}><EyeIcon visible={showPassword} /></button>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                <input type="checkbox" checked={isTOSAccepted} onChange={(e) => setIsTOSAccepted(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                <span style={{ fontSize: '14px', color: '#000' }}>
                  {/* Option B wording (2026-09-26): agree to the Terms of Use and EULA,
                      acknowledge the Privacy Policy. Revisit with legal counsel. */}
                  I have read and agree to the{' '}
                  <TOSpopup title="Terms of Use" setIsAccepted={setIsTOSAccepted}>
                    <TermsOfUseModalContent />
                  </TOSpopup>
                  {' '}and <LegalTabLink doc={EULA} />, and acknowledge the <LegalTabLink doc={PRIVACY_POLICY} />


                </span>
              </div>

              {error && (
                <p style={{ color: '#D0021B', fontSize: '13px', marginTop: '15px', textAlign: 'center', maxWidth: '320px', lineHeight: '1.4' }}>
                  {error}
                </p>
              )}

              <button style={submitButtonStyle} onClick={handleRealSignUp}>
                Create Account
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#2B4B6F', marginBottom: '20px' }}>Enter the 6-digit code sent to <b>{email}</b></p>
              <div style={{ position: 'relative', marginBottom: '25px' }}>
                <input type="text" maxLength="6" autoFocus value={verifCode} disabled={isVerifying} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} onChange={(e) => setVerifCode(e.target.value.replace(/\D/g, ""))}
                  style={{ position: 'absolute', opacity: 0, width: '100%', height: '55px', zIndex: 2, cursor: isVerifying ? 'not-allowed' : 'pointer' }} />
                {[...Array(6)].map((_, i) => {
                  // LOGIC: Is this the specific box waiting for input?
                  const isActive = isFocused && verifCode.length === i;
                  const hasValue = verifCode[i] !== undefined;

                  return (
                    <div
                      key={i}
                      className="verification-box"
                      style={{
                        ...boxStyle,
                        // If active, use a thicker, darker border. If not, use standard logic.
                        border: isActive ? '2px solid #2B4B6F' : `1px solid ${isVerifying ? '#D1DEE6' : '#2B4B6F'}`,
                        // If active, add a slight shadow glow
                        boxShadow: isActive ? '0 0 5px rgba(43, 75, 111, 0.3)' : 'none',
                        // If it's a placeholder, make it gray. If it's a real value, make it black.
                        color: hasValue ? 'black' : '#D1DEE6',
                        display: 'inline-block',
                        verticalAlign: 'top'
                      }}
                    >
                      {/* Show the typed number OR the placeholder index (1-6) */}
                      {hasValue ? verifCode[i] : i + 1}
                    </div>
                  );
                })}
              </div>
              {error && !isVerifying && <p style={{ color: '#D0021B', fontSize: '14px', marginBottom: '10px' }}>{error}</p>}
              {isVerifying && <p style={{ color: '#4A8EB3' }}>Creating account...</p>}
              <p style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowVerification(false)}>Back to form</p>
              {showVerification && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                  <span
                    onClick={handleClearEntry}
                    style={{ cursor: 'pointer', textDecoration: 'underline', color: '#2B4B6F' }}
                  >
                    Clear Entry
                  </span>
                  <span
                    onClick={handleResendCode}
                    style={{ cursor: 'pointer', textDecoration: 'underline', color: '#2B4B6F' }}
                  >
                    Request New Code
                  </span>
                </div>
              )}
            </div>
          )}

          {!showVerification && (
            <span style={{ color: '#2B4B6F', cursor: 'pointer', marginTop: '20px', fontSize: '14px', textDecoration: 'underline' }} onClick={() => navigate('/login')}>
              Already have an account? Sign in.
            </span>
          )}
        </div>
      </AuthShell>
    );
  }

  // =========================================================
  // DEVELOPMENT RENDERING
  // =========================================================

  const inputContainerStyle = {
    marginBottom: '15px',
    textAlign: 'center',
    width: '100%'
  };

  const labelStyle = {
    display: 'block',
    color: '#2B4B6F',
    fontSize: '16px',
    marginBottom: '5px',
    fontWeight: '500'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    height: '42px',
    borderRadius: '8px',
    border: '2px solid #D1DEE6',
    backgroundColor: '#F8FAFC',
    textAlign: 'center',
    fontSize: '14px',
    boxSizing: 'border-box'
  };

  const passwordInputStyle = { ...inputStyle, paddingRight: '36px' };

  const eyeBtnStyle = {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    color: '#2B4B6F',
    display: 'flex',
    alignItems: 'center',
  };

  const submitButtonStyle = {
    backgroundColor: '#4A8EB3',
    color: 'white',
    border: 'none',
    padding: '12px 40px',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '20px'
  };

  return (
    <AuthShell title="Create Your Account">
      <style>{mobileStyles}</style>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>

        {/* First & Last Name Row */}
        <div className="mobile-row" style={{ display: 'flex', gap: '15px', width: '100%', maxWidth: '320px', marginBottom: '15px' }}>
          <div style={{ flex: 1 }}>
            <label className="mobile-label" style={labelStyle}>First Name</label>
            <input
              className="mobile-input"
              style={inputStyle}
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              placeholder="First Name"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="mobile-label" style={labelStyle}>Last Name</label>
            <input
              className="mobile-input"
              style={inputStyle}
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              placeholder="Last Name"
            />
          </div>
        </div>

        {/* Age & Zipcode Row */}
        <div className="mobile-row" style={{ display: 'flex', gap: '15px', width: '100%', maxWidth: '320px', marginBottom: '15px' }}>
          <div style={{ flex: 1 }}>
            <label className="mobile-label" style={labelStyle}>Date of Birth</label>
            <input
              className="mobile-input"
              type="date"
              style={inputStyle}
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              // Prevent future dates
              max={new Date().toISOString().split("T")[0]}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="mobile-label" style={labelStyle}>ZIP code</label>
            <input
              className="mobile-input"
              style={inputStyle}
              value={zipcode}
              maxLength={5}
              onChange={(e) => setZipcode(e.target.value)}
              placeholder="00000"
            />
          </div>
        </div>

        {/* Email & Password */}
        <div style={{ ...inputContainerStyle, maxWidth: '320px' }}>
          <label style={labelStyle}>Email Address</label>
          <input
            style={inputStyle}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
          />
        </div>

        <div style={{ ...inputContainerStyle, maxWidth: '320px' }}>
          <label style={labelStyle}>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? "text" : "password"}
              style={passwordInputStyle}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              maxLength={15}
            />
            <button style={eyeBtnStyle} onClick={() => setShowPassword(!showPassword)} type="button" tabIndex={-1}>
              <EyeIcon visible={showPassword} />
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
          <input
            type="checkbox"
            checked={isTOSAccepted}
            onChange={(e) => setIsTOSAccepted(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '14px', color: '#000' }}>
            {/* Option B wording (2026-09-26): agree to the Terms of Use and EULA,
                acknowledge the Privacy Policy. Revisit with legal counsel. */}
            I have read and agree to the{' '}
            <TOSpopup title="Terms of Use" setIsAccepted={setIsTOSAccepted}>
              <TermsOfUseModalContent />
            </TOSpopup>
            {' '}and <LegalTabLink doc={EULA} />, and acknowledge the <LegalTabLink doc={PRIVACY_POLICY} />

          </span>
        </div>

        {/* Error Message Display */}
        {error && (
          <p style={{
            color: '#D0021B',
            fontSize: '13px',
            marginTop: '15px',
            textAlign: 'center',
            maxWidth: '320px',
            lineHeight: '1.4'
          }}>
            {error}
          </p>
        )}

        <button style={submitButtonStyle} onClick={handleDevSignUp}>
          Create Account
        </button>

        <span
          style={{ color: '#2B4B6F', cursor: 'pointer', marginTop: '20px', fontSize: '14px', textDecoration: 'underline' }}
          onClick={() => navigate('/login')}>
          Already have an account? Sign in.
        </span>
      </div>
    </AuthShell>
  );
};

export default SignUpFront;